import { readStorage, writeStorage } from "./storage"

/** A locally stored, unsent note (the composer's "Drafts" list). */
export interface NoteDraft {
  id: string
  content: string
  updatedAt: number
}

export const MAX_DRAFTS = 20
const KEY = "noteDrafts"

/**
 * Pure upsert: replaces the draft with the same id (or prepends a new one),
 * keeps the list newest first and capped so a runaway autosave cannot fill the
 * user's localStorage.
 */
export function upsertDraft(list: NoteDraft[], draft: NoteDraft): NoteDraft[] {
  const without = list.filter((item) => item.id !== draft.id)
  return [draft, ...without].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_DRAFTS)
}

export function removeDraft(list: NoteDraft[], id: string): NoteDraft[] {
  return list.filter((item) => item.id !== id)
}

/** Drops malformed entries (hand edited storage, older versions). */
export function parseDrafts(raw: unknown): NoteDraft[] {
  if (!Array.isArray(raw)) return []
  const drafts: NoteDraft[] = []
  for (const item of raw) {
    if (!item || typeof item !== "object") continue
    const candidate = item as Partial<NoteDraft>
    if (typeof candidate.id !== "string" || typeof candidate.content !== "string") continue
    if (!candidate.content.trim()) continue
    drafts.push({
      id: candidate.id,
      content: candidate.content,
      updatedAt: typeof candidate.updatedAt === "number" ? candidate.updatedAt : 0,
    })
  }
  return drafts.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_DRAFTS)
}

export function firstLine(content: string, max = 90): string {
  const line = (content.split("\n").find((part) => part.trim()) ?? "").trim()
  return line.length > max ? `${line.slice(0, max - 1)}…` : line
}

export function newDraftId(): string {
  const random = globalThis.crypto?.randomUUID?.()
  if (random) return random
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function loadDrafts(): NoteDraft[] {
  return parseDrafts(readStorage<unknown>(KEY, []))
}

export function storeDrafts(drafts: NoteDraft[]): void {
  writeStorage(KEY, drafts)
}
